<script>
    import { navigate } from '../router.js';
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import { userStore } from '../store';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Chip from '../components/Chip.svelte';
    import Stat from '../components/Stat.svelte';
    import Banner from '../components/Banner.svelte';
    import EmptyState from '../components/EmptyState.svelte';

    export let goToRecommendations;

    let faceAnalyses = [];
    let userProfile = {};
    let isLoading = true;
    let errorMessage = '';
    let username = '';
    let loadingRecommendationsFor = null;

    function displayUsername(value) {
        return value && String(value).toLowerCase() !== 'null' ? value : '';
    }

    async function loadProfile(authData) {
        const profileResponse = await fetch(`/api/profile/${encodeURIComponent(authData.userId)}`, {
            credentials: 'include'
        });

        if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            userProfile = profileData.userInfo;
            faceAnalyses = profileData.faceAnalyses;
            username = displayUsername(authData.username) || username;
        } else {
            errorMessage = 'Failed to load profile data';
        }
    }

    onMount(async () => {
        try {
            const response = await fetch('/api/is_authenticated', { credentials: 'include' });

            if (response.ok) {
                const authData = await response.json();
                username = displayUsername(authData.username);
                userStore.set({ isAuthenticated: true, username, userId: authData.userId });
                await loadProfile(authData);
            } else {
                const existingUser = get(userStore);
                if (existingUser.isAuthenticated && existingUser.userId) {
                    await loadProfile(existingUser);
                } else {
                    errorMessage = 'Please log in to view your account history';
                }
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            const existingUser = get(userStore);
            if (existingUser.isAuthenticated && existingUser.userId) {
                try {
                    await loadProfile(existingUser);
                } catch (fallbackError) {
                    console.error('Error loading profile from existing session:', fallbackError);
                    errorMessage = 'An error occurred while loading your profile';
                }
            } else {
                errorMessage = 'An error occurred while loading your profile';
            }
        } finally {
            isLoading = false;
        }
    });

    function startNewAnalysis() {
        navigate('face-analysis');
    }

    function getSkinType() {
        const skinType = userProfile.skinType;
        if (Array.isArray(skinType) && skinType.length > 0) {
            return skinType[0] || 'normal';
        }
        return skinType || 'normal';
    }

    function getConcernNames(analysis) {
        return parseConcerns(analysis.concerns)
            .map(concern => concern.concern)
            .filter(Boolean);
    }

    async function viewRecommendations(analysis) {
        const concerns = getConcernNames(analysis);
        if (concerns.length === 0) {
            errorMessage = 'No skin concerns found for that analysis.';
            return;
        }

        loadingRecommendationsFor = analysis.pictureId;

        try {
            const response = await fetch('/api/recommendation_engine', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    areasOfImprovement: concerns,
                    skinType: getSkinType(),
                    skinAllergies: Array.isArray(userProfile.allergies) ? userProfile.allergies : [],
                    avoidIngredients: Array.isArray(userProfile.allergies) ? userProfile.allergies : [],
                    age: userProfile.age || 25,
                    zipcode: userProfile.location || '78681',
                    routineType: userProfile.routineType || 'mid_tier',
                    userId: userProfile.userid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to load recommendations');
            }

            const recommendations = await response.json();
            goToRecommendations(recommendations);
        } catch (error) {
            console.error('Error loading recommendations from profile:', error);
            errorMessage = 'Unable to load recommendations for this analysis.';
        } finally {
            loadingRecommendationsFor = null;
        }
    }

    function formatDate(isoDate) {
        if (!isoDate) return 'Unknown date';

        const date = new Date(isoDate);
        const today = new Date();

        if (date.toDateString() === today.toDateString()) {
            return 'Today';
        }

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    function parseConcerns(concernsStr) {
        try {
            if (!concernsStr) return [];
            let parsed = concernsStr;
            if (typeof concernsStr === 'string') {
                parsed = JSON.parse(concernsStr);
            }
            // Handle double-encoded JSON strings
            if (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }
            if (!Array.isArray(parsed)) return [];
            // Normalize: if items are strings, convert to {concern, score} objects
            return parsed.map(item => {
                if (typeof item === 'string') {
                    return { concern: item.replace(/_/g, ' '), score: null };
                }
                return item;
            });
        } catch (e) {
            console.error('Error parsing concerns:', e);
            return [];
        }
    }

    // --- presentation helpers -------------------------------------------
    // The screen used to crash outright when the profile endpoint answered
    // with a shape that had no `faceAnalyses` array. Normalising here is not
    // a change to the fetch; it is what lets the empty state render instead
    // of a blank page.
    $: analyses = Array.isArray(faceAnalyses) ? faceAnalyses : [];
    $: profile = userProfile || {};
    $: hasAnalyses = analyses.length > 0;
    $: sensitivities = Array.isArray(profile.allergies) ? profile.allergies.filter(Boolean) : [];
    $: latestConcerns = hasAnalyses ? parseConcerns(analyses[0].concerns) : [];

    // Signing out is an expected state with its own next step, not a failure.
    $: needsSignIn = !!errorMessage && /log in/i.test(errorMessage);
    // An error raised while fetching recommendations should not wipe out a
    // history that loaded fine. Only an error with nothing behind it owns the
    // screen; the rest sit in a banner above the content.
    $: blockingError = !!errorMessage && !hasAnalyses;
    $: stateOwnsCta = isLoading || blockingError || !hasAnalyses;

    function titleCase(value) {
        const text = String(value || '').replace(/_/g, ' ');
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
    }

    function scoreLabel(concern) {
        return concern.score != null ? `${Math.round(concern.score * 100)}%` : null;
    }
</script>

<style>
    .hero {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: var(--space-10);
        align-items: end;
        margin-bottom: var(--space-12);
    }

    .eyebrow {
        display: block;
        color: var(--rose);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin-bottom: var(--space-4);
    }

    h1, h2, h3 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        margin: 0;
    }

    h1 {
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
        color: var(--cream);
    }

    h2 { font-size: var(--text-xl); color: var(--cream); }
    h3 { font-size: var(--text-lg); color: var(--cream); }

    h4 {
        margin: 0 0 var(--space-3);
        font-family: var(--font-body);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    .lead {
        max-width: var(--prose-max);
        margin: var(--space-4) 0 0;
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
    }

    .section {
        margin-top: var(--section-gap);
    }

    .section-head {
        display: flex;
        align-items: baseline;
        gap: var(--space-4);
        margin-bottom: var(--space-6);
        padding-bottom: var(--space-4);
        border-bottom: 1px solid var(--border);
    }

    .section-count {
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    /* --- who am I -------------------------------------------------------- */
    .stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--space-8);
    }

    .profile-extra {
        margin-top: var(--space-8);
        padding-top: var(--space-6);
        border-top: 1px solid var(--border);
    }

    .tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
    }

    /* --- an analysis ----------------------------------------------------- */
    /* The section rules and the stack rule meet on .analysis-item, and their
       top margins collapse to the larger of the two, so a card that opens a
       section gets the section gap and a card inside one gets the stack gap. */
    .analysis-item + .analysis-item {
        margin-top: var(--space-6);
    }

    .analysis-item .section-head {
        margin-top: var(--section-gap);
    }

    .analysis-item:first-child .section-head {
        margin-top: 0;
    }

    .analysis-head {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: var(--space-6);
        align-items: center;
        padding-bottom: var(--space-6);
        border-bottom: 1px solid var(--border);
    }

    .analysis-body {
        display: grid;
        gap: var(--space-8);
        margin-top: var(--space-6);
    }

    .featured .analysis-body {
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        align-items: start;
    }

    .block + .block {
        margin-top: var(--space-6);
    }

    .muted {
        margin: 0;
        color: var(--text-muted);
        font-size: var(--text-sm);
        line-height: var(--leading-relaxed);
    }

    /* --- what it recommended --------------------------------------------- */
    .products {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: var(--space-px);
        background: var(--border);
        border: 1px solid var(--border);
    }

    .product {
        display: grid;
        grid-template-columns: var(--space-8) minmax(0, 1fr);
        gap: var(--space-4);
        padding: var(--space-4);
        background: var(--bg);
    }

    .product-index {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        line-height: var(--leading-normal);
        color: var(--rose);
        text-align: center;
        border-right: 1px solid var(--rose-line);
    }

    .product-name {
        color: var(--cream);
        font-size: var(--text-sm);
        line-height: var(--leading-snug);
    }

    .product-routine {
        margin-top: var(--space-1);
        font-size: var(--text-3xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--rose);
    }

    .product-instructions {
        margin: var(--space-2) 0 0;
        font-size: var(--text-xs);
        font-weight: var(--weight-light);
        line-height: var(--leading-relaxed);
        color: var(--text-muted);
    }

    .data-actions {
        margin-top: var(--space-6);
    }

    /* --- loading --------------------------------------------------------- */
    .loading-head {
        margin-bottom: var(--space-8);
    }

    .skeleton-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: var(--space-8);
        margin-bottom: var(--space-10);
    }

    .skeleton {
        display: block;
        height: var(--space-3);
        background: var(--hover-veil);
        animation: pulse 2s var(--ease) infinite;
    }

    .skeleton-stats .skeleton {
        height: var(--space-10);
    }

    .skeleton-lines .skeleton + .skeleton {
        margin-top: var(--space-4);
    }

    .skeleton-lines .skeleton:nth-child(1) { width: 72%; }
    .skeleton-lines .skeleton:nth-child(2) { width: 54%; }
    .skeleton-lines .skeleton:nth-child(3) { width: 63%; }

    @keyframes pulse {
        0%, 100% { opacity: 0.45; }
        50% { opacity: 1; }
    }

    @media (max-width: 768px) {
        .hero,
        .analysis-head {
            grid-template-columns: 1fr;
            align-items: start;
        }

        .hero { gap: var(--space-8); }

        /* The hero action is the page's one call to action and can fill the
           column. A per-analysis control repeated down the page should not. */
        .analysis-action { justify-self: start; }
    }
</style>

<div class="u-page">
    <header class="hero">
        <div>
            <span class="eyebrow">Account and history</span>
            <h1>{username ? `${username}'s skin, on record.` : 'Your skin, on record.'}</h1>
            <p class="lead">
                Your saved skin profile, every face analysis you have taken, and the products each
                one recommended.
            </p>
        </div>
        {#if !stateOwnsCta}
            <Button on:click={startNewAnalysis}>New face analysis</Button>
        {/if}
    </header>

    {#if isLoading}
        <Card>
            <div class="loading-head">
                <span class="eyebrow">Loading</span>
                <h2>Opening your account</h2>
            </div>
            <div class="skeleton-stats" aria-hidden="true">
                <span class="skeleton"></span>
                <span class="skeleton"></span>
                <span class="skeleton"></span>
            </div>
            <div class="skeleton-lines" aria-hidden="true">
                <span class="skeleton"></span>
                <span class="skeleton"></span>
                <span class="skeleton"></span>
            </div>
        </Card>
    {:else if blockingError}
        {#if needsSignIn}
            <EmptyState
                title="Sign in to see your account history."
                body="Your skin profile and past analyses are saved to your account. Log in from the top of the page, then come back here."
            />
        {:else}
            <EmptyState
                title="We could not open your account history."
                body={errorMessage}
            >
                <Button on:click={startNewAnalysis}>Take a face analysis</Button>
            </EmptyState>
        {/if}
    {:else if !hasAnalyses}
        <EmptyState
            title="No face analyses yet."
            body="Take your first analysis to build a skin profile. Everything it finds, and everything it recommends, is kept here."
        >
            <Button on:click={startNewAnalysis}>Take your first analysis</Button>
        </EmptyState>
    {:else}
        {#if errorMessage}
            <Banner tone="danger" title="Something went wrong">{errorMessage}</Banner>
        {/if}

        <section class="section">
            <div class="section-head">
                <h2>Your skin profile</h2>
            </div>
            <Card>
                <div class="stats">
                    <Stat align="start" value={titleCase(getSkinType())} label="Skin type" />
                    <Stat align="start" value={analyses.length} label="Analyses on file" />
                    <Stat align="start" value={latestConcerns.length} label="Concerns tracked" caption="From your latest analysis" />
                </div>
                {#if sensitivities.length > 0}
                    <div class="profile-extra">
                        <h4>Ingredients you avoid</h4>
                        <div class="tags">
                            {#each sensitivities as sensitivity}
                                <Chip tone="caution">{titleCase(sensitivity)}</Chip>
                            {/each}
                        </div>
                    </div>
                {/if}
            </Card>
        </section>

        <section class="section">
            {#each analyses as analysis, index}
                <div class="analysis-item">
                {#if index === 0}
                    <div class="section-head">
                        <h2>Latest analysis</h2>
                    </div>
                {:else if index === 1}
                    <div class="section-head">
                        <h2>Earlier analyses</h2>
                        <span class="section-count">{analyses.length - 1} saved</span>
                    </div>
                {/if}

                <Card>
                    <div class:featured={index === 0}>
                        <div class="analysis-head">
                            <h3>{formatDate(analysis.date)}</h3>
                            <div class="analysis-action">
                            <Button
                                variant="secondary"
                                size="sm"
                                on:click={() => viewRecommendations(analysis)}
                                loading={loadingRecommendationsFor === analysis.pictureId}
                                busyLabel="Loading recommendations"
                            >
                                View recommendations
                            </Button>
                            </div>
                        </div>

                        <div class="analysis-body">
                            <div>
                                <div class="block">
                                    <h4>Concerns detected</h4>
                                    <div class="tags">
                                        {#each parseConcerns(analysis.concerns) as concern}
                                            <Chip value={scoreLabel(concern)}>{concern.concern}</Chip>
                                        {:else}
                                            <p class="muted">No concerns detected in this analysis.</p>
                                        {/each}
                                    </div>
                                </div>

                                {#if parseConcerns(analysis.strengths).length > 0}
                                    <div class="block">
                                        <h4>Skin strengths</h4>
                                        <div class="tags">
                                            {#each parseConcerns(analysis.strengths) as strength}
                                                <Chip tone="success">{strength.concern}</Chip>
                                            {/each}
                                        </div>
                                    </div>
                                {/if}
                            </div>

                            <div class="block">
                                <h4>What it recommended</h4>
                                {#if analysis.recommendations && analysis.recommendations.length > 0}
                                    <ol class="products">
                                        {#each analysis.recommendations as product, productIndex}
                                            <li class="product">
                                                <span class="product-index">{productIndex + 1}</span>
                                                <div>
                                                    <div class="product-name">{product.ProductName}</div>
                                                    {#if product.RoutineType}
                                                        <div class="product-routine">{product.RoutineType} routine</div>
                                                    {/if}
                                                    {#if product.UsageInstructions}
                                                        <p class="product-instructions">{product.UsageInstructions}</p>
                                                    {/if}
                                                </div>
                                            </li>
                                        {/each}
                                    </ol>
                                {:else}
                                    <p class="muted">
                                        Nothing saved from this analysis yet. Use View recommendations to
                                        generate a set from it.
                                    </p>
                                {/if}
                            </div>
                        </div>
                    </div>
                </Card>
                </div>
            {/each}
        </section>
    {/if}

    <!-- Outside the {#if} chain on purpose. Apple 5.1.1(v) wants deletion
         easy to find, and the states above own the screen: an account with no
         analyses yet, or a history that failed to load, would otherwise have
         no route to it. -->
    {#if !needsSignIn}
        <section class="section">
            <div class="section-head">
                <h2>Your data</h2>
            </div>
            <Card>
                <p class="muted">
                    You can delete your account and everything on it, from here, at
                    any time. It takes effect immediately. The
                    <a href="/privacy">privacy policy</a> says what is held and what
                    happens to each part of it.
                </p>
                <div class="data-actions">
                    <Button variant="secondary" size="sm" on:click={() => navigate('delete-account')}>
                        Delete my account
                    </Button>
                </div>
            </Card>
        </section>
    {/if}
</div>
